import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SignatureData, buildBchUri } from '@/lib/signature';
import { generateSignatureHtml } from '@/lib/generateSignatureHtml';
import { useQrDataUrl } from '@/hooks/useQrDataUrl';
import { useToast } from '@/hooks/useToast';
import { track } from '@/lib/analytics';
import { Copy, Check, Code2, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SignaturePreviewProps {
  data: SignatureData;
  /** Called the first time the user copies (used to count "signatures created"). */
  onCopied?: () => void;
}

export function SignaturePreview({ data, onCopied }: SignaturePreviewProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const bchUri = useMemo(() => buildBchUri(data), [data]);
  const qrDataUrl = useQrDataUrl(bchUri, 256);

  const origin =
    typeof window !== 'undefined' ? window.location.origin : '';

  const html = useMemo(
    () => generateSignatureHtml(data, { qrDataUrl, origin, sigId: data.sigId }),
    [data, qrDataUrl, origin],
  );

  const copyRich = async () => {
    try {
      if (
        typeof ClipboardItem !== 'undefined' &&
        navigator.clipboard &&
        'write' in navigator.clipboard
      ) {
        const item = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([htmlToText(data)], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(html);
      }
      setCopied(true);
      toast({
        title: 'Signature copied!',
        description: 'Paste it into your email signature settings.',
      });
      // Aggregate, anonymous analytics (respects opt-in consent).
      track('copied');
      track('template', { template: data.template });
      onCopied?.();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Try the “Copy HTML” option instead.',
        variant: 'destructive',
      });
    }
  };

  const copySource = async () => {
    try {
      await navigator.clipboard.writeText(html);
      setCopiedHtml(true);
      toast({ title: 'HTML source copied' });
      setTimeout(() => setCopiedHtml(false), 2000);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={copyRich} className="flex-1 min-w-[180px] gap-2" size="lg">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied!' : 'Copy Signature'}
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={() => setShowCode((s) => !s)}
          className="gap-2"
        >
          {showCode ? <Eye className="h-4 w-4" /> : <Code2 className="h-4 w-4" />}
          {showCode ? 'Preview' : 'HTML'}
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2">
          <span className="text-xs font-medium text-muted-foreground">
            {showCode ? 'HTML source' : 'Live preview'}
          </span>
          {showCode && (
            <Button
              variant="ghost"
              size="sm"
              onClick={copySource}
              className="h-7 gap-1.5 text-xs"
            >
              {copiedHtml ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              Copy HTML
            </Button>
          )}
        </div>

        {showCode ? (
          <pre className="max-h-[420px] overflow-auto bg-background p-4 text-[11px] leading-relaxed text-foreground/80">
            <code>{html}</code>
          </pre>
        ) : (
          <div
            className="overflow-auto p-6"
            style={{
              backgroundColor: data.bgColor,
              backgroundImage:
                'linear-gradient(45deg, rgba(128,128,128,0.06) 25%, transparent 25%, transparent 75%, rgba(128,128,128,0.06) 75%), linear-gradient(45deg, rgba(128,128,128,0.06) 25%, transparent 25%, transparent 75%, rgba(128,128,128,0.06) 75%)',
              backgroundSize: '16px 16px',
              backgroundPosition: '0 0, 8px 8px',
            }}
          >
            {/* Rendered signature: content is generated by our own trusted
                template function from user-entered data with escaped values. */}
            <div
              className={cn('min-w-fit')}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>
        )}
      </Card>

      <p className="text-center text-xs text-muted-foreground">
        Tip: In Gmail go to Settings → General → Signature, then paste. In Outlook
        use Signatures → New, then paste.
      </p>
    </div>
  );
}

function htmlToText(data: SignatureData): string {
  const lines = [
    data.name,
    [data.title, data.business].filter(Boolean).join(', '),
    data.tagline,
    data.phone && `Tel: ${data.phone}`,
    data.email && `Email: ${data.email}`,
    data.website && data.website,
    data.address,
    data.bchAddress && `BCH: ${data.bchAddress}`,
  ].filter(Boolean);
  return lines.join('\n');
}
